import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Calendar, ArrowLeft, Plus, Edit2, Trash2, BarChart3 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../../components/shared/Modal";

export default function ManageEvents({ onBack, onViewPnL } = {}) {
  const [events, setEvents] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [accountants, setAccountants] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", start_date: "", end_date: "", child_max_age: 17, walkin_service_charge_pct: 0, merchant_contributions: [], accountant_ids: [] });
  const { authAxios } = useAuth();

  useEffect(() => { fetchEvents(); fetchMerchants(); fetchAccountants(); }, []);

  const fetchEvents = async () => { const res = await authAxios.get("/events"); setEvents(res.data); };
  const fetchMerchants = async () => { const res = await authAxios.get("/merchants/active"); setMerchants(res.data); };
  const fetchAccountants = async () => { const res = await authAxios.get("/accountants"); setAccountants(res.data.filter(a => a.is_active)); };

  const handleAdd = async () => {
    if (!form.name || !form.start_date || !form.end_date) return toast.error("Fill required fields");
    if (form.accountant_ids.length === 0) return toast.error("Invite at least one accountant");
    try { await authAxios.post("/events", form); toast.success("Event created"); setShowAdd(false); setForm({ name: "", location: "", start_date: "", end_date: "", child_max_age: 17, walkin_service_charge_pct: 0, merchant_contributions: [], accountant_ids: [] }); fetchEvents(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleUpdate = async () => {
    if (!form.name || !form.start_date || !form.end_date) return toast.error("Fill required fields");
    if (form.accountant_ids.length === 0) return toast.error("Invite at least one accountant");
    try { await authAxios.put(`/events/${editItem.id}`, form); toast.success("Event updated"); setEditItem(null); setForm({ name: "", location: "", start_date: "", end_date: "", child_max_age: 17, walkin_service_charge_pct: 0, merchant_contributions: [], accountant_ids: [] }); fetchEvents(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => { if (!window.confirm("Delete this event?")) return; await authAxios.delete(`/events/${id}`); toast.success("Deleted"); fetchEvents(); };

  const toggleMerchant = (merchantId, isSelected) => {
    if (isSelected) setForm({ ...form, merchant_contributions: form.merchant_contributions.filter(mc => mc.merchant_id !== merchantId) });
    else setForm({ ...form, merchant_contributions: [...form.merchant_contributions, { merchant_id: merchantId, contribution_margin: 20 }] });
  };

  const updateMargin = (merchantId, margin) => {
    setForm({ ...form, merchant_contributions: form.merchant_contributions.map(mc => mc.merchant_id === merchantId ? { ...mc, contribution_margin: parseFloat(margin) || 0 } : mc) });
  };

  const openEdit = (event) => {
    setEditItem(event);
    setForm({ name: event.name, location: event.location || "", start_date: event.start_date, end_date: event.end_date, child_max_age: event.child_max_age ?? 17, walkin_service_charge_pct: event.walkin_service_charge_pct ?? 0, merchant_contributions: event.merchant_contributions || [], accountant_ids: event.accountant_ids || [] });
  };

  const getStatusBadge = (status) => {
    if (status === "active") return "bg-green-100 text-green-600";
    if (status === "upcoming") return "bg-amber-100 text-amber-600";
    return "bg-slate-100 text-slate-400";
  };

  const renderEventForm = (onSubmit, submitLabel) => (
    <>
      <input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Event Name *" className="w-full p-3 rounded-xl border mb-3" data-testid="event-name" autoComplete="off" />
      <input value={form.location} onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))} placeholder="Location (optional)" className="w-full p-3 rounded-xl border mb-3" data-testid="event-location" autoComplete="off" />
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label className="text-xs text-slate-500">Start Date *</label><input type="date" value={form.start_date} onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value }))} className="w-full p-3 rounded-xl border" data-testid="event-start" /></div>
        <div><label className="text-xs text-slate-500">End Date *</label><input type="date" value={form.end_date} onChange={e => setForm(prev => ({ ...prev, end_date: e.target.value }))} className="w-full p-3 rounded-xl border" data-testid="event-end" /></div>
      </div>
      <div className="mb-3">
        <label className="text-xs text-slate-500">Child max age (for family ticket pricing)</label>
        <input
          type="number"
          min="0"
          max="120"
          value={form.child_max_age ?? 17}
          onChange={e => setForm(prev => ({ ...prev, child_max_age: parseInt(e.target.value || "0", 10) }))}
          className="w-full p-3 rounded-xl border"
          data-testid="event-child-max-age"
        />
        <p className="text-[11px] text-slate-400 mt-1">Family-linked members below this age price as "Child"; at or above, they price as Family/Adult.</p>
      </div>
      <div className="mb-4">
        <label className="text-sm font-medium text-slate-700 mb-1 block">Walk-in surcharge (%)</label>
        <input type="number" min="0" max="100" step="0.5" value={form.walkin_service_charge_pct ?? 0}
          onChange={e => setForm(prev => ({ ...prev, walkin_service_charge_pct: parseFloat(e.target.value || "0") }))}
          className="w-full p-3 rounded-xl border" data-testid="event-walkin-service-charge" />
        <p className="text-[11px] text-slate-400 mt-1">Added on top of the ticket price for tickets sold at the walk-in desk. 0 = no charge.</p>
      </div>
      <div className="mb-4">
        <p className="text-sm font-medium text-slate-700 mb-2">Invite Merchants (with Contribution Margin %) — optional</p>
        <div className="max-h-48 overflow-y-auto space-y-2 border rounded-xl p-3">
          {merchants.map(m => {
            const selected = form.merchant_contributions.find(mc => mc.merchant_id === m.id);
            return (
              <div key={m.id} className="flex items-center gap-2">
                <input type="checkbox" checked={!!selected} onChange={() => toggleMerchant(m.id, !!selected)} className="rounded" />
                <span className="flex-1 text-sm">{m.name}</span>
                {selected && (<div className="flex items-center gap-1"><input type="number" min="0" max="100" value={selected.contribution_margin} onChange={e => updateMargin(m.id, e.target.value)} className="w-16 p-1 rounded border text-center text-sm" /><span className="text-xs text-slate-400">%</span></div>)}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mb-4">
        <p className="text-sm font-medium text-slate-700 mb-2">Invite Accountants *</p>
        <div className="max-h-48 overflow-y-auto space-y-2 border rounded-xl p-3">
          {accountants.length === 0 ? (<p className="text-slate-400 text-sm text-center py-2">No active accountants available</p>) : (
            accountants.map(a => {
              const selected = form.accountant_ids.includes(a.id);
              return (
                <div key={a.id} className="flex items-center gap-2">
                  <input type="checkbox" checked={selected} onChange={() => { if (selected) setForm({ ...form, accountant_ids: form.accountant_ids.filter(id => id !== a.id) }); else setForm({ ...form, accountant_ids: [...form.accountant_ids, a.id] }); }} className="rounded" />
                  <span className="flex-1 text-sm">{a.name}</span>
                  <span className="text-xs text-slate-400">{a.email}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
      <button onClick={onSubmit} className="w-full py-3 rounded-full bg-[#f5a623] text-white font-bold" data-testid="save-event">{submitLabel}</button>
    </>
  );

  return (
    <div data-testid="manage-events">
      <div className="flex items-center justify-between mb-6">
        {onBack && <button onClick={onBack} className="flex items-center gap-2 text-slate-600"><ArrowLeft className="w-5 h-5" /> Events</button>}
        <button onClick={() => { setShowAdd(true); setForm({ name: "", location: "", start_date: "", end_date: "", child_max_age: 17, walkin_service_charge_pct: 0, merchant_contributions: [], accountant_ids: [] }); }} className="w-10 h-10 rounded-full bg-[#f5a623] text-white flex items-center justify-center" data-testid="add-event-btn"><Plus className="w-5 h-5" /></button>
      </div>
      {events.length === 0 ? (
        <div className="text-center py-20 text-slate-400"><Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>No events yet</p></div>
      ) : (
        <div className="space-y-3">
          {events.map(e => (
            <div key={e.id} className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <div><h3 className="font-bold text-slate-800">{e.name}</h3>{e.location && <p className="text-slate-500 text-sm">{e.location}</p>}<p className="text-slate-400 text-sm">{e.start_date} - {e.end_date}</p></div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(e.status)}`}>{e.status?.toUpperCase()}</span>
              </div>
              <div className="text-sm text-slate-500 mb-1">{e.merchant_contributions?.length || 0} merchant(s): {e.merchant_contributions?.map(mc => `${mc.merchant_name || 'Unknown'} (${mc.contribution_margin}%)`).join(", ")}</div>
              <div className="text-sm text-slate-400 mb-2">{e.accountant_ids?.length || 0} accountant(s) invited</div>
              <div className="flex gap-2">
                {e.settlement_status !== "settled" && <button onClick={() => openEdit(e)} className="flex items-center gap-1 text-blue-500 text-sm"><Edit2 className="w-4 h-4" /> Edit</button>}
                {onViewPnL && <button onClick={() => onViewPnL(e.id)} className="flex items-center gap-1 text-[#1e3a5f] text-sm"><BarChart3 className="w-4 h-4" /> P&L</button>}
                {e.settlement_status !== "settled" && <button onClick={() => handleDelete(e.id)} className="flex items-center gap-1 text-red-400 text-sm ml-auto"><Trash2 className="w-4 h-4" /></button>}
                {e.settlement_status === "settled" && <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-600">Settled</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {showAdd && <Modal title="Create Event" onClose={() => setShowAdd(false)}>{renderEventForm(handleAdd, "Create Event")}</Modal>}
      {editItem && <Modal title="Edit Event" onClose={() => setEditItem(null)}>{renderEventForm(handleUpdate, "Update Event")}</Modal>}
    </div>
  );
}
